import { Button, Divider, Header, Loader, Segment } from "semantic-ui-react";
import PlaceholderUntilLoaded from "../../../../../../components/global_components/PlaceholderUntilLoaded";
import SharePopup from "../../../../components/SharePopup";
import { StyledReactMarkdown } from "../../../../../../components/StyledReactMarkdown";
import { useNotifyContentDetails } from "../../../../hooks/useNotifyContent";
import { Link, useParams } from "react-router-dom";
import { useUserAgent } from "../../../../../../hooks/useUserAgent";
import { useState } from "react";
import { useMobileAppDownloadLinks } from "../../../../../../hooks/useMobileAppDownloadLinks";
import MobileStoreLinks from "../../../../components/MobileStoreLinks";
import "../../../../components/public_view_preview.scss";

import { useSelector } from "react-redux";
import identitySelectors from "../../../../../../selectors/identity";
import { useAnonymousToken } from "../../../../../auth/hooks/useAnonymousToken";
import { NotifyContentSubscribeBanner } from "./NotifyContentSubscribeBanner";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { ZonedDateTime } from "../../../../../../components/global_components/ZonedDateTime";
import TwoWayCommunicationSection from "../../../../components/public_view/TwoWayCommunicationSection";
import AppSettings from "../../../../../../config";
import Page403 from "../../../../../../components/global_components/Page403";
import classNames from "classnames";

const notifyContentVisibility = {
  PUBLIC: "Public",
  PRIVATE: "Private",
  UNLISTED: "Unlisted",
};

export const NotifyContentPublicDetailsCard = () => {
  const params = useParams<{ site: string; id: string }>();
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);

  const [isTwoWayCommunicationModalOpen, setIsTwoWayCommunicationModalOpen] = useState(false);

  const isLoggedIn = useSelector(identitySelectors.isLoggedIn);
  const { isLoggedIn: isAnonymouslyLoggedIn } = useAnonymousToken();

  const { notifyContent, areContentDetailsLoading } = useNotifyContentDetails({ id: Number(params.id) });
  const { appleLink, googleLink } = useMobileAppDownloadLinks();
  const { isGoMobile } = useUserAgent();

  // Function to use native mobile sharing, if on mobile, or to open the custom share menu, if on desktop
  const handleMobileShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    // If navigator.share is available (typically on mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          url: `${AppSettings.GOGOV_BASE_API_URL}/core/notify/public/share/${params.site}/${params.id}`,
        });
      } catch (error) {
        console.error("Error sharing:");
      }
    } else {
      // If navigator.share is not available (typically on desktop browsers)
      setIsSharePopupOpen(true);
    }
  };

  const publicRelatedSubscriptionGroups = notifyContent?.subscriptionGroups.filter(
    (group) => group.visibility === notifyContentVisibility.PUBLIC,
  );

  const { subscriberContactMethods, isSubscriberContactMethodsLoading } =
    useNotifySubscriberContactMethodsForLoggedInUser();

  const subscriptionGroupsAlreadySubscribed = subscriberContactMethods?.map((method) => method.subscriptionGroups);

  // Ensure subscriptionGroupsToSubscribe is empty if subscriptionGroupsAlreadySubscribed is empty
  const unsubscribedGroups = subscriptionGroupsAlreadySubscribed?.length
    ? (isLoggedIn || isAnonymouslyLoggedIn()
        ? notifyContent?.subscriptionGroups
        : publicRelatedSubscriptionGroups
      )?.filter(
        (group) =>
          !subscriptionGroupsAlreadySubscribed?.some((subscribedGroup) =>
            subscribedGroup.some((g) => g.id === group.id),
          ),
      )
    : publicRelatedSubscriptionGroups;

  const hasAccess =
    notifyContent?.subscriptionGroups.length == 0 ||
    notifyContent?.subscriptionGroups.some((group) => group.visibility === notifyContentVisibility.PUBLIC) ||
    // one of the contact methods is a part of a subscription group this post is a part of
    subscriptionGroupsAlreadySubscribed?.some((subscribedGroup) =>
      subscribedGroup.some((g) => notifyContent?.subscriptionGroups.some((group) => group.id === g.id)),
    );

  const isPageLoading = areContentDetailsLoading || isSubscriberContactMethodsLoading;

  if (!hasAccess && !isPageLoading) {
    return <Page403 />;
  }

  return (
    <>
      <Loader
        className={classNames({
          "tw-hidden": !isPageLoading,
        })}
        active
      >
        Loading...
      </Loader>
      <div className=' tw-mx-3 tw-mt-[38px] tw-flex  tw-flex-col tw-items-center tw-p-0 '>
        <Segment className=' tw-w-full tw-max-w-3xl !tw-p-[21px]'>
          <div>
            <PlaceholderUntilLoaded isLoading={areContentDetailsLoading} lineCount={1}>
              <Button
                as={Link}
                to={`/${params.site}/public/notify/posts`}
                icon='arrow left'
                content='All Posts'
                className='no-padding-button'
              />
            </PlaceholderUntilLoaded>
            <div>
              <PlaceholderUntilLoaded isLoading={areContentDetailsLoading} lineCount={3}>
                <Header className='tw-mb-0 tw-mt-[7px] tw-text-[1.75rem]' as='h1'>
                  <Header.Content className={"responsive-word-wrap"}>{notifyContent?.headline}</Header.Content>
                </Header>
                <Header as='h3' className={"responsive-word-wrap tw-mt-[7px] tw-text-[1.125rem]"}>
                  {notifyContent?.tagline}
                  <Divider />
                  <Header.Subheader className='tw-mt-1 tw-font-black tw-text-black tw-opacity-100'>
                    {"Published: "}
                    <ZonedDateTime
                      formatString={"MMM d, yyyy @ h:mm a"}
                      value={notifyContent?.publishedAt ?? new Date()}
                    ></ZonedDateTime>
                  </Header.Subheader>
                </Header>

                <Header.Subheader className='tw-mb-[14px]'>
                  <p className='tw-mr-1 tw-inline tw-opacity-70'>Related Subscription Groups: </p>
                  {!isLoggedIn && !isAnonymouslyLoggedIn ? (
                    <div className='tw-inline '>
                      {publicRelatedSubscriptionGroups?.map((group, index) => (
                        <span key={group.id}>
                          <Link to={`/${params.site}/public/notify/posts?ids=[${group.id}]`}>{group.name}</Link>
                          {index < publicRelatedSubscriptionGroups.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className='tw-inline '>
                      {notifyContent?.subscriptionGroups?.map((group, index) => (
                        <span key={group.id}>
                          <Link to={`/${params.site}/public/notify/posts?groupIds=${group.id}`}>{group.name}</Link>
                          {index < notifyContent.subscriptionGroups.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  )}
                </Header.Subheader>
                <SharePopup
                  trigger={
                    <Button
                      basic
                      icon='share'
                      content='Share'
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleMobileShare(event)}
                    />
                  }
                  headline={notifyContent?.headline ?? ""}
                  position='bottom left'
                  contentUrl={window.location.href}
                  open={isSharePopupOpen}
                  setOpen={setIsSharePopupOpen}
                />
              </PlaceholderUntilLoaded>
              <Divider />
              <PlaceholderUntilLoaded isLoading={areContentDetailsLoading} lineCount={10}>
                <StyledReactMarkdown>{notifyContent?.body ?? ""}</StyledReactMarkdown>
              </PlaceholderUntilLoaded>
              {notifyContent?.classification && (isLoggedIn || isAnonymouslyLoggedIn()) && (
                <div>
                  <TwoWayCommunicationSection
                    modalOpen={isTwoWayCommunicationModalOpen}
                    headline={notifyContent.headline}
                    classificationName={notifyContent.classification?.classificationText}
                    classificationId={notifyContent.classification?.classificationId}
                    classificationActive={notifyContent.classification.active}
                    openModal={() => {
                      setIsTwoWayCommunicationModalOpen(true);
                    }}
                    closeModal={() => {
                      setIsTwoWayCommunicationModalOpen(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Segment>
        {unsubscribedGroups && unsubscribedGroups?.length > 0 && (
          <NotifyContentSubscribeBanner
            unSubscribedGroups={unsubscribedGroups}
            subscriberContactMethodIds={subscriberContactMethods?.map((method) => method.id)}
          />
        )}
        {!isGoMobile && (
          <>
            <PlaceholderUntilLoaded isLoading={areContentDetailsLoading} lineCount={1}>
              <MobileStoreLinks appStoreHref={appleLink} googlePlayStoreHref={googleLink} />
            </PlaceholderUntilLoaded>
          </>
        )}
      </div>
    </>
  );
};
